import { makeStyles } from 'tss-react/mui';
import { getStyleVariables } from 'styles/vars';
import { Button, Theme, DialogActions } from '@mui/material';
import { GVTypography } from 'components/lib';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { InviteSummary } from 'store/queries/organizations/types';
import { InviteError } from '@verify/shared-utils';
import styles from './AddUserSummary.module.css';

type AddUserSummaryProps = {
  inviteSummary: InviteSummary;
  backToEdit: () => void;
  onClose: () => void;
};

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);

  return {
    scrollBar: { ...styleVariables.scrollBar, scrollbarGutter: 'auto' },
  };
});

type FailedUserCardProps = {
  email: string;
  error: string;
};

const FailedUserCard = ({ email, error }: FailedUserCardProps) => {
  return (
    <div className={styles.card}>
      <GVTypography className={styles.email}>{email}</GVTypography>
      <GVTooltip title={error}>
        <InfoOutlinedIcon className={styles.info} />
      </GVTooltip>
    </div>
  );
};

const AddUserSummary = ({ inviteSummary, backToEdit, onClose }: AddUserSummaryProps) => {
  const { classes } = useStyles();
  const { successfulInvites, failedInvites } = inviteSummary;
  const invalidEmails = failedInvites.filter((invite) => invite.error === InviteError.Invalid);

  return (
    <div className={styles.form}>
      <div className={styles.titles}>
        <GVTypography variant="subtitle2" fontWeight={600}>
          Summary
        </GVTypography>
        <GVTypography variant="subtitle1" fontWeight={400}>
          {`${successfulInvites}/${successfulInvites + failedInvites.length} users successfully added to your team`}
        </GVTypography>
      </div>
      {failedInvites.length > 0 && (
        <>
          <div className={styles.warning}>
            <WarningAmberIcon className={styles.error} />
            <GVTypography variant="subtitle1" fontWeight={400}>
              The following users could not be added to your Team
            </GVTypography>
          </div>
          <div className={`${styles.errors} ${classes.scrollBar}`}>
            {failedInvites.map((invite) => {
              return <FailedUserCard email={invite.email} error={invite.error} />;
            })}
          </div>
        </>
      )}

      <DialogActions className={styles.actions}>
        {invalidEmails.length > 0 && (
          <Button
            color="inherit"
            id="bulk-add-team-members-form-cancel-button"
            onClick={backToEdit}
            disabled={failedInvites.length === 0}
          >
            Back to Edit
          </Button>
        )}
        <Button
          id="dialog-ok"
          type="submit"
          color="secondary"
          form="bulk-add-team-members-form"
          variant="contained"
          onClick={onClose}
        >
          Okay
        </Button>
      </DialogActions>
    </div>
  );
};

export default AddUserSummary;
